<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { Plus } from 'lucide-vue-next'
import { useAppShellStore } from '@/store/AppShellStore'
import { gradebookApi, scheduleApi } from '@/api'
import type { ScheduleItem, ClassDetails } from '@/api'
import {
  AppButton,
  paletteForKey,
  paletteFromColour,
  type PaletteColour,
  type PaletteHue,
} from '@/design-system/components'
import PageHeader from '@/components/today/PageHeader.vue'
import ClassSortToggle from '@/components/classes/ClassSortToggle.vue'
import ClassCard from '@/components/classes/ClassCard.vue'
import AddClassCard from '@/components/classes/AddClassCard.vue'
import CreateClassModal from '@/components/modals/CreateClassModal.vue'
import { useAnalytics } from '@/composables/useAnalytics'

const router = useRouter()
const appShell = useAppShellStore()
const { capture } = useAnalytics()

function openClass(classId: string, index: number) {
  capture('class_opened', { class_index: index })
  router.push(`/classes/${classId}`)
}

const classes = computed(() => appShell.sortedClasses)
const totalStudents = computed(() =>
  appShell.classes.reduce((n, c) => n + (c.studentCount ?? 0), 0),
)

function hueFor(c: { colour?: string | null; id?: string }): PaletteHue {
  if (c.colour) return paletteFromColour(c.colour as PaletteColour)
  return paletteForKey(c.id ?? '')
}

// ── Per-class stats (avg + to-grade), loaded progressively from gradebooks ──
interface CardStat {
  avg: number | null
  toGrade: number | null
}
const stats = ref<Record<string, CardStat>>({})

async function loadStats() {
  await Promise.all(
    appShell.classes.map(async (c) => {
      if (!c.id) return
      try {
        const gb = await gradebookApi.getGradebook(c.id)
        const pcts = gb.studentGrades
          .map((sg) => sg.finalGrade?.percentage)
          .filter((p): p is number => p != null)
        const avg = pcts.length ? Math.round(pcts.reduce((a, b) => a + b, 0) / pcts.length) : null
        const toGrade = gb.submissions.filter((s) => !s.markedAt).length
        stats.value[c.id] = { avg, toGrade }
      } catch {
        stats.value[c.id] = { avg: null, toGrade: null }
      }
    }),
  )
}

// ── Next lesson per class (one week-ahead schedule fetch) ───────────────────
const nextByClass = ref<Record<string, string>>({})
function nextLabel(start: Date): string {
  const now = new Date()
  const sameDay = start.toDateString() === now.toDateString()
  const time = `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`
  const day = sameDay ? 'Today' : start.toLocaleDateString('en-GB', { weekday: 'short' })
  return `${day} · ${time}`
}
async function loadNext() {
  const now = new Date()
  const to = new Date(now)
  to.setDate(now.getDate() + 13)
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  try {
    const res = await scheduleApi.getSchedule({ from: iso(now), to: iso(to) })
    const earliest: Record<string, Date> = {}
    for (const it of (res.items ?? []) as ScheduleItem[]) {
      if (it.type !== 'lesson' || !it.classId) continue
      const start = new Date(it.dateTime)
      if (start < now) continue
      if (!earliest[it.classId] || start < earliest[it.classId]!) earliest[it.classId] = start
    }
    nextByClass.value = Object.fromEntries(
      Object.entries(earliest).map(([id, d]) => [id, nextLabel(d)]),
    )
  } catch {
    // leave empty — cards show em-dash
  }
}

// ── Create class ─────────────────────────────────────────────────────────────
const showCreate = ref(false)
function onCreated(cls: ClassDetails) {
  appShell.loadClasses(true)
  if (cls.id) router.push(`/classes/${cls.id}`)
}

onMounted(async () => {
  await appShell.loadClasses()
  loadStats()
  loadNext()
})
</script>

<template>
  <div class="overview">
    <PageHeader
      :eyebrow="`${appShell.classes.length} ${appShell.classes.length === 1 ? 'class' : 'classes'} · ${totalStudents} students`"
      title="Classes"
    >
      <template #right>
        <AppButton variant="primary" size="md" @click="showCreate = true">
          <Plus :size="15" /> New class
        </AppButton>
      </template>
    </PageHeader>

    <div class="overview__sortrow">
      <span class="overview__eyebrow">All classes · {{ classes.length }}</span>
      <ClassSortToggle />
    </div>

    <div class="overview__grid">
      <ClassCard
        v-for="(c, i) in classes"
        :key="c.id"
        :hue="hueFor(c)"
        :name="c.name ?? ''"
        :student-count="c.studentCount ?? 0"
        :avg="c.id ? (stats[c.id]?.avg ?? null) : null"
        :to-grade="c.id ? (stats[c.id]?.toGrade ?? null) : null"
        :next-label="c.id ? (nextByClass[c.id] ?? null) : null"
        @open="c.id && openClass(c.id, i)"
      />
      <AddClassCard @add="showCreate = true" />
    </div>

    <CreateClassModal v-model="showCreate" @created="onCreated" />
  </div>
</template>

<style scoped>
.overview {
  max-width: 1180px;
  margin: 0 auto;
  padding: 26px 36px 56px;
  font-family: var(--font-sans);
}
.overview__sortrow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.overview__eyebrow {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: var(--ink-400);
}
.overview__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
  align-items: start;
}
@media (max-width: 1100px) {
  .overview {
    padding: 22px 20px 48px;
  }
}
</style>
